/**
 * StoryBuilder — root wrapper for the story creation experience.
 * Handles socket/audio init, error display, book overlay, and chat.
 */

import React, { useEffect, useRef, useState } from "react";
import { View, Text, Pressable, Platform, StyleSheet, Modal, BackHandler } from "react-native";
import { colors, textStyle } from "../styles";
import { useSocketStore } from "../store/socketStore";
import { useKeepAwake } from "../hooks/useKeepAwake";
import { useStoryStore } from "../store/storyStore";
import { useComicStore } from "../store/comicStore";
import { useConfigStore } from "../store/configStore";
import { DEFAULT_IMAGE_STYLE, DEFAULT_OUTPUT_FORMAT } from "@amzn/a4k-creator-studio-shared";
import { useAudioStore } from "../store/audioStore";
import { playMp3 } from "../audio/FTUEAudioPlayer";
import { GREETING_AUDIO } from "../audio/greetings";
import StoryView from "./story/StoryView";
import RestartIcon from "../assets/icons/ic-restart.svg";
import { useNavigation } from "../navigation/useNavigation";
import BookOverlay from "./BookOverlay";
import ChatPanel from "./chat/ChatPanel";
import { trackView, trackButton, View as MetricView, Button } from "../services/metrics";

export type TabId = "story" | "book" | "clear";

interface StoryBuilderProps {
  initialTab?: TabId;
}

export default function StoryBuilder({ initialTab = "story" }: StoryBuilderProps) {
  const { navigate, goBack } = useNavigation();
  const { initSocket, isVoiceActive } = useSocketStore();
  const { initAudio } = useAudioStore();
  useKeepAwake('story-builder', isVoiceActive);

  const [bookVisible, setBookVisible] = useState(initialTab === "book");

  const showBook = () => {
    trackButton(MetricView.StoryBuilder, Button.StoryBuilder.TabBook);
    setBookVisible(true);
  };

  const hideBook = () => {
    trackButton(MetricView.StoryBuilder, Button.StoryBuilder.TabStory);
    setBookVisible(false);
  };

  // Android hardware back → leave the builder. Registered once for the whole
  // lifetime (refs avoid stale closures); when the book is open it returns false
  // so BookView's own handler closes the book first.
  const goBackRef = useRef(goBack);
  goBackRef.current = goBack;
  const bookVisibleRef = useRef(bookVisible);
  bookVisibleRef.current = bookVisible;
  useEffect(() => {
    if (Platform.OS === "web") return;
    const sub = BackHandler.addEventListener("hardwareBackPress", () => {
      if (bookVisibleRef.current) return false; // let BookView handle it
      useSocketStore.getState().stopVoice();
      goBackRef.current();
      return true;
    });
    return () => sub.remove();
  }, []);

  useEffect(() => {
    trackView(MetricView.StoryBuilder);
    initSocket().catch(console.error);
    initAudio().catch(console.error);

    if (!useSocketStore.getState().autoStartVoice) return;

    let started = false;
    const tryStart = () => {
      if (started) return;
      // Re-read (don't consume at mount) so essError clearing it cancels the start.
      const { isConnected, storyLoading, autoStartVoice, entryGreeting } = useSocketStore.getState();
      if (!autoStartVoice) { unsubSocket(); unsubAudio(); return; }
      const { audioProcessor } = useAudioStore.getState();
      // Gate voice start on storyLoading=false so we never open a Bedrock
      // session before loadESSProject has finished restoring state to Redis.
      // Without this, the voice prompt is built with stale/empty story context.
      if (isConnected && audioProcessor && !storyLoading) {
        started = true;
        useSocketStore.setState({ autoStartVoice: false, entryGreeting: null });
        unsubSocket();
        unsubAudio();
        useSocketStore.getState().startVoice().then(() => {
          if (entryGreeting) {
            playMp3(GREETING_AUDIO[entryGreeting]);
          }
        });
      }
    };

    const unsubSocket = useSocketStore.subscribe(tryStart);
    const unsubAudio = useAudioStore.subscribe(tryStart);
    tryStart();

    return () => {
      unsubSocket(); unsubAudio();
      // Stop voice on unmount — covers all navigation away from builder
      // (gallery, back button, deep link, etc.) so Muse never stays listening.
      useSocketStore.getState().stopVoice();
    };
  }, []);

  // Book invalidation on story/config edits is server-driven: the service
  // resets genState to idle and emits comicInvalidated (see comicHandlers).

  // Auto-open the book overlay when generation starts, and close it if
  // generation fails (genState reverts to idle).
  useEffect(() => {
    let prev = useComicStore.getState().genState;
    return useComicStore.subscribe((state) => {
      const next = state.genState;
      if (prev !== "generating_prompts" && next === "generating_prompts") {
        setBookVisible(true);
      } else if (prev !== "idle" && next === "idle") {
        setBookVisible(false);
      }
      prev = next;
    });
  }, []);

  // Muse goes inactive whenever the create page isn't visible (book overlay up).
  // On return to the create page it stays inactive until the user taps to activate;
  // the first tap while generating resumes Muse in the server's read-only mode.
  // A suspend is promoted to a stop here (and on unmount) so it can't outlive the
  // create page and auto-resume on a later reconnect from elsewhere.
  useEffect(() => {
    const { isVoiceActive, voiceSuspended } = useSocketStore.getState();
    if (bookVisible && (isVoiceActive || voiceSuspended)) {
      useSocketStore.getState().stopVoice();
    }
  }, [bookVisible]);

  useEffect(() => {
    return () => {
      const { isVoiceActive, voiceSuspended } = useSocketStore.getState();
      if (isVoiceActive || voiceSuspended) {
        useSocketStore.getState().stopVoice();
      }
    };
  }, []);

  const [showClearModal, setShowClearModal] = useState(false);

  const doClear = () => {
    setShowClearModal(false);
    trackButton(MetricView.StoryBuilder, Button.StoryBuilder.ClearStory);
    const { isVoiceActive, voiceSuspended } = useSocketStore.getState();
    if (isVoiceActive || voiceSuspended) {
      useSocketStore.getState().stopVoice();
    }
    useStoryStore.getState().clearContent();
    useConfigStore.getState().replaceAll({ imageStyle: DEFAULT_IMAGE_STYLE, outputFormat: DEFAULT_OUTPUT_FORMAT });
    useSocketStore.getState().emitEvent("clearStoryContent");
  };

  const handleClear = () => {
    if (Platform.OS === "web") {
      if (confirm("This will clear the current story. Ready to start a new one?")) doClear();
    } else {
      setShowClearModal(true);
    }
  };

  return (
    <View style={styles.root}>
      <StoryView
        onShowBook={showBook}
        onShowGallery={() => { useSocketStore.getState().stopVoice(); navigate("gallery"); }}
        onClear={handleClear}
        onClose={() => { useSocketStore.getState().stopVoice(); goBack(); }}
      />

      <BookOverlay visible={bookVisible} onClose={hideBook} />

      <ChatPanel />

      <Modal visible={showClearModal} transparent animationType="fade" onRequestClose={() => setShowClearModal(false)}>
        <View style={styles.clearBackdrop}>
          <Pressable style={StyleSheet.absoluteFill} onPress={() => setShowClearModal(false)} />
          <View style={styles.clearModal}>
            <Text style={styles.clearTitle}>Start a new story?</Text>
            <Text style={styles.clearSubtitle}>This will clear your current progress.</Text>
            <Pressable style={styles.clearBtnConfirm} onPress={doClear}>
              <RestartIcon width={20} height={20} color={colors.white} />
              <Text style={styles.clearBtnText}>Clear</Text>
            </Pressable>
          </View>
        </View>
      </Modal>
    </View>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: colors.bg,
    position: "relative",
  },
  clearBackdrop: {
    flex: 1,
    backgroundColor: "rgba(0,0,0,0.6)",
    alignItems: "center",
    justifyContent: "center",
  },
  clearModal: {
    backgroundColor: colors.galleryBg,
    borderRadius: 36,
    paddingHorizontal: 48,
    paddingTop: 48,
    paddingBottom: 32,
    alignItems: "center",
  },
  clearTitle: {
    ...textStyle.headingMedium,
    fontSize: 32,
    color: colors.bg,
    marginBottom: 8,
  },
  clearSubtitle: {
    ...textStyle.headingMedium,
    fontSize: 20,
    color: colors.bg,
    opacity: 0.7,
    marginBottom: 32,
  },
  clearBtnConfirm: {
    height: 40,
    borderRadius: 9999,
    backgroundColor: colors.danger,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: 24,
    gap: 8,
  },
  clearBtnText: {
    ...textStyle.heading,
    fontSize: 18,
    color: colors.white,
  },
});
